import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import Dashboard from './pages/Dashboard';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/LoginPage';
import CashierSignInGuard from './guards/CashierSignInGuard';
import AuthGuard from './guards/AuthGuard';
import MainLayout from './layouts/MainLayout';
import { useUserPermissionAndMenu } from './contexts/UserPermissionAndMenuProvider';
import { loadComponentByName, getRoutesFromMenus } from './utils/dynamicRouteLoader';
import Loading from './components/Loading';
import { useAuth } from './contexts/AuthProvider';
import DateGuard from './guards/DateGuard';
import PawningMandatePrint from './pages/PawningMandatePrint';
import FdCertificatePrint from './pages/FdCertificatePrint';
import CustomerProfile from './pages/CustomerProfile';
import PrintDesigner from './pages/PrintDesigner';
import GuarantorInsurancePrint from './pages/GuarantorInsurancePrint';
import FDManagementPrint from './pages/FDManagementPrint';
import LoanSchedulePrint from './pages/LoanSchedulePrint';
import FdRenewalLetterPrint from './pages/FdRenewalLetterPrint';
import MemberMeetingScan from './pages/MemberMeetingScan';

const DynamicRoutes = () => {
    const { userMenus, loading } = useUserPermissionAndMenu();
    const location = useLocation();

    // Show loading while data is being fetched
    if (loading || !userMenus) {
        return <Loading message="Please wait, loading system configurations..." />;
    }

    // Get routes from menus that have component defined
    const routeMenus = getRoutesFromMenus(userMenus);

    return (
        <Routes key={location.pathname}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pawning-mandate" element={<PawningMandatePrint />} />
            <Route path="/fd-certificate-print" element={<FdCertificatePrint />} />
            <Route path="/guarantor-insurance-print" element={<GuarantorInsurancePrint />} />
            <Route path="/customers/:customerNumber" element={<CustomerProfile />} />
            <Route path="/print-designer" element={<PrintDesigner />} />
            <Route path="/fd-management/print" element={<FDManagementPrint />} />
            <Route path="/loan-schedule-print" element={<LoanSchedulePrint />} />
            <Route path="/fd-renewal-letter" element={<FdRenewalLetterPrint />} />
            {/* Manual route for Auto Loan Recovery */}
            {/* Dynamically generated routes from backend menus */}
            {routeMenus.map((menu) => {
                const Component = loadComponentByName(menu.component ?? undefined);
                if (!Component || !menu.to_url) return null;
                return (
                    <Route
                        key={menu.id}
                        path={menu.to_url}
                        element={
                            <Suspense fallback={<Loading message="Please wait, Loading page..." />}>
                                {/* Add CashierSignInGuard for transaction routes */}
                                {menu.cashier_required ? (
                                    <CashierSignInGuard>
                                        <Component key={menu.to_url} />
                                    </CashierSignInGuard>
                                ) : (
                                    <Component key={menu.to_url} />
                                )}
                            </Suspense>
                        }
                    />
                );
            })}

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
};

const ProtectedApp = () => {
    return (
        <AuthGuard>
            <DateGuard>
                <MainLayout>
                    <DynamicRoutes />
                </MainLayout>
            </DateGuard>
        </AuthGuard>
    );
};

const AppRoutes = () => {
    const { isAuthenticated, loading: authLoading } = useAuth();

    // Show loading while checking auth
    if (authLoading) {
        return <Loading message="Initializing..." />;
    }

    // Render based on authentication state
    return (
        <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
            <Route path="/member-meeting-scan/:instanceId" element={isAuthenticated ? <MemberMeetingScan /> : <Navigate to="/login" replace />} />
            <Route path="*" element={isAuthenticated ? <ProtectedApp /> : <Navigate to="/login" replace />} />
        </Routes>
    );
};

export default AppRoutes;